import React, { useCallback, useEffect, useState } from 'react'

import { Picker, Select, TextInput } from '../../../module'
import { type ActiveFilterProps } from '../../SortColumn/SortColumn'
import styles from './_column-filter.module.scss'
import { type TextInputNumber } from '../../Form/TextInput'
import { c } from '../../../translations/LibraryTranslationService'
import { type SelectDisplayOption } from '../../Selects/SelectDisplay/SelectDisplay'

export type FilterOptions = {
  id: number
  text: string
  value: string
}
type optionsFilterHeader = SelectDisplayOption & {
  type?: 'currency' | 'percentage'
}
type ColumnFilterType = {
  /** The array of options to be displayed in the picker */
  filterOptions?: FilterOptions[]
  /** Optional callback triggers when any change occurs in the toggle options or on input change */
  columnFilterCallback?: (activeFilter: ActiveFilterProps) => void
  /** Unique column key to perform the sort */
  tableKey: string
  /** Optional suffix added to the values */
  suffix?: string
  /** Optional prefix added to the values */
  prefix?: string
  /** Optional props for the text input */
  textInputProps?: TextInputNumber
  options?: optionsFilterHeader[]
  /** Optionally enable filtering by sub-metrics (the change values) */
  enableFilterByChangeValues?: boolean
  /** SetStateAction to set the input value */
  setInputValue: React.Dispatch<
    React.SetStateAction<{
      value: number | string
      actualValue: number | string
    }>
  >
  /** SetStateAction to set the currently selected filter column data */
  setColumnFilterData: React.Dispatch<React.SetStateAction<ActiveFilterProps>>
  /** Optional object to display the default selected filter values */
  defaultFilter?: {
    selectedFilterOption: FilterOptions
    value: number | string
  }
  /** The currently selected filter option*/
  selectedFilterOption?: optionsFilterHeader
  /** SetStateAction to set the currently selected filter option*/
  setSelectedFilterOption: React.Dispatch<
    React.SetStateAction<optionsFilterHeader>
  >
  /** Default label to the filter input. */
  headerText: string
  setVisible?: React.Dispatch<React.SetStateAction<boolean>>
  /** Optional prop to add a test id to the ColumnFilter for QA testing */
  qaTestId?: string
}

const defaultFilterOptions = [
  {
    id: 1,
    text: '>',
    value: 'gt',
  },
  {
    id: 2,
    text: '≥',
    value: 'gte',
  },
  {
    id: 3,
    text: '<',
    value: 'lt',
  },
  {
    id: 4,
    text: '≤',
    value: 'lte',
  },
]
const ColumnFilter = ({
  tableKey,
  prefix = '',
  suffix = '',
  filterOptions = defaultFilterOptions,
  columnFilterCallback,
  textInputProps,
  setInputValue,
  setColumnFilterData,
  defaultFilter,
  enableFilterByChangeValues = false,
  options,
  headerText,
  selectedFilterOption,
  setSelectedFilterOption,
  setVisible,
  qaTestId = 'column-filter',
}: ColumnFilterType): React.JSX.Element => {
  const [optionSelected, setOptionSelected] = useState<
    FilterOptions | undefined
  >()

  useEffect(() => {
    setOptionSelected(defaultFilter?.selectedFilterOption ?? filterOptions[0])
    setInputValue({ value: defaultFilter?.value ?? '', actualValue: '' })
  }, [
    defaultFilter?.selectedFilterOption,
    defaultFilter?.value,
    filterOptions,
    setInputValue,
  ])

  const updateInputState = useCallback(
    (filter: ActiveFilterProps) => {
      columnFilterCallback?.(filter)
    },
    [columnFilterCallback],
  )

  const filterValueOptions = enableFilterByChangeValues
    ? {
        labelText: selectedFilterOption?.label ?? headerText,
        tableKey: selectedFilterOption?.name ?? tableKey,
        type: selectedFilterOption?.type ?? textInputProps?.numberFormat?.type,
        prefix: selectedFilterOption?.type
          ? selectedFilterOption?.type === 'currency'
            ? '$'
            : ''
          : prefix,
        suffix: selectedFilterOption?.type
          ? selectedFilterOption?.type === 'percentage'
            ? '%'
            : ''
          : suffix,
      }
    : {
        labelText: headerText,
        tableKey: tableKey,
        type: textInputProps?.numberFormat?.type,
        prefix,
        suffix,
      }
  return (
    <div className={styles.columnFilterContainer} qa-test-id={qaTestId}>
      {enableFilterByChangeValues && options && (
        <Select
          options={options}
          selectedItem={
            selectedFilterOption ?? {
              name: 'select_option',
              label: c('selectOption'),
            }
          }
          optionKeyName={'name'}
          labelKeyName={'label'}
          noMenuPortal
          onChange={(selectedOption) => {
            setVisible?.(true)
            setSelectedFilterOption(selectedOption)
            const filterValueOptions = {
              labelText: selectedOption.label,
              tableKey: selectedOption.name,
              type: selectedOption?.type ?? textInputProps?.numberFormat?.type,
              prefix: selectedOption?.type
                ? selectedOption?.type === 'currency'
                  ? '$'
                  : ''
                : prefix,
              suffix: selectedOption?.type
                ? selectedOption?.type === 'percentage'
                  ? '%'
                  : ''
                : suffix,
            }
            let stateData: ActiveFilterProps = {}
            const actualValue = filterValueOptions?.suffix
              ? Number(
                  (parseFloat(String(textInputProps?.value)) / 100).toFixed(4),
                )
              : textInputProps?.value
            setInputValue({
              value: textInputProps?.value || '',
              actualValue: actualValue || '',
            })
            if (filterValueOptions?.labelText) {
              stateData = {
                [filterValueOptions?.labelText]: {
                  id: optionSelected?.id || 1,
                  text: optionSelected?.text || '>',
                  condition: optionSelected?.value || 'gt',
                  keyField: filterValueOptions?.tableKey,
                  value: textInputProps?.value || '',
                  actualValue: actualValue,
                  label: `${filterValueOptions?.labelText} ${optionSelected?.text} ${filterValueOptions?.prefix}${textInputProps?.value ?? 0}${filterValueOptions?.suffix}`,
                  type: filterValueOptions?.type || 'currency',
                },
              }
            }
            updateInputState(stateData)
            setColumnFilterData(stateData)
          }}
          labelProps={{ label: c('filterBy') }}
        />
      )}
      <Picker
        options={filterOptions}
        state={optionSelected}
        labelText={c('setAFilter')}
        stateName='value'
        callout={(_, value) => {
          let stateData: ActiveFilterProps = {}
          const option = filterOptions.find((f) => f.value === value)
          setOptionSelected(option ?? filterOptions[0])
          if (filterValueOptions?.labelText) {
            stateData = {
              [filterValueOptions?.labelText]: {
                id: option?.id || 1,
                text: option?.text || '>',
                condition: option?.value || 'gt',
                value: textInputProps?.value || '',
                keyField: filterValueOptions?.tableKey,
                actualValue: textInputProps?.value,
                label: `${filterValueOptions?.labelText} ${option?.text} ${filterValueOptions?.prefix}${
                  textInputProps?.value ?? 0
                }${filterValueOptions?.suffix}`,
                type: filterValueOptions?.type || 'currency',
              },
            }
          }
          updateInputState(stateData)
          setColumnFilterData(stateData)
        }}
      />

      <TextInput
        type='number'
        value={textInputProps?.value ?? ''}
        {...{
          ...textInputProps,
          labelText: filterValueOptions?.labelText,
        }}
        callout={(_, value) => {
          let stateData: ActiveFilterProps = {}
          const actualValue = filterValueOptions?.suffix
            ? Number((parseFloat(value?.toString()) / 100).toFixed(4))
            : value
          setInputValue({ value: value, actualValue: actualValue })
          if (filterValueOptions?.labelText) {
            stateData = {
              [filterValueOptions?.labelText]: {
                id: optionSelected?.id || 1,
                text: optionSelected?.text || '>',
                condition: optionSelected?.value || 'gt',
                keyField: filterValueOptions?.tableKey,
                value: value || '',
                actualValue: actualValue,
                label: `${filterValueOptions?.labelText} ${optionSelected?.text} ${filterValueOptions?.prefix}${value}${filterValueOptions?.suffix}`,
                type: filterValueOptions?.type || 'currency',
              },
            }
          }
          updateInputState(stateData)
          setColumnFilterData(stateData)
        }}
        {...(filterValueOptions?.type
          ? {
              numberFormat: {
                type: filterValueOptions?.type,
                currencySymbol:
                  filterValueOptions?.type === 'currency'
                    ? textInputProps?.numberFormat?.currencySymbol
                    : undefined,
              },
              inputLabel:
                filterValueOptions?.type === 'percentage' ? '%' : undefined,
            }
          : {})}
      />
    </div>
  )
}

export default ColumnFilter
